// eventChatPageHelpers — EventChatPage.js から純粋な定数/ヘルパを分離（挙動不変）。
// 状態非依存のモジュールレベル純粋部分（URL構築・書式・アセットヘルパ生成等）。

import { buildEventChatSearchUrl, createFetchJsonSafe } from '../../common/chat/eventlist/eventRequestUtils';
import { ADMIN_MESSAGE_FORMATS } from '../../../constants/messageFormats';
import { createEventAssetHelpers } from '../../common/chat/shared/eventAssetHelpers';
import { toDateInput, formatDateJP } from '../../../utils/datetime';
import { getSocket } from '../../../lib/socket';
import { normalizeCastingProgressSummary } from '../../../utils/castingProgress';

export const DEBUG = false;
export const dlog = (...args) => { if (DEBUG) console.debug('[EventChatPage]', ...args); };
export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const API_URL = process.env.REACT_APP_API_URL;
export const ADMIN_EVENTS_HEAD_URL = `${API_URL}/api/admin/event-chat/available-events/head`;
export const ADMIN_EVENTS_PAGE_URL = (offset) => `${API_URL}/api/admin/event-chat/available-events/${offset}`;
export const ADMIN_PARTICIPANTS_URL = (eventId) => `${API_URL}/api/admin/event-chat/participants/${eventId}`;
export const ADMIN_GROUP_EVENTS_URL = (eventId) => `${API_URL}/api/admin/event-chat/group-events/${eventId}`;
export const ADMIN_REPRESENTATIVE_EVENT_BY_THREAD_URL = (threadId) => `${API_URL}/api/admin/event-chat/representative-event/${threadId}`;
export const EVENT_NOTIFICATION_SILENCE_URL = (eventId) => `${API_URL}/api/admin/events/${eventId}/notification-silence`;
export const EVENT_TARGET_ROW_CLASSES = {
  root: 'event-target-row chat-target-row chat-target-row--event',
  left: 'event-target-row__media chat-target-row__media event-list__flyer-shell',
  center: 'event-target-row__text chat-target-row__text',
  nameLine: 'event-target-row__title-line chat-target-row__title-line',
  name: 'event-list__title',
  preview: 'event-list__last-preview',
  below: 'event-target-row__notice chat-target-row__below',
  right: 'event-target-row__side chat-target-row__side',
  time: 'event-list__time',
  badge: 'event-list__unread-badge',
};
export const ADMIN_EVENT_SEARCH_PAGE_LIMIT = 50;

export const ADMIN_SEARCH_API = ({
  q = '',
  message = '',
  startDate = '',
  endDate = '',
  offset = 0,
  limit = ADMIN_EVENT_SEARCH_PAGE_LIMIT,
  snapshotToken = '',
} = {}) => {
  return buildEventChatSearchUrl({
    apiUrl: API_URL,
    role: 'admin',
    q,
    message,
    startDate,
    endDate,
    offset,
    limit,
    snapshotToken,
  });
};

export const buildAdminEventNotesListUrl = (eventId, offset = 0, limit = 20) =>
  `${API_URL}/api/admin/event-notes/${eventId}?offset=${offset}&limit=${limit}`;

export const MESSAGE_IMAGE_PATH = process.env.REACT_APP_MESSAGE_IMAGE_PATH;
export const MESSAGE_FILE_PATH = process.env.REACT_APP_MESSAGE_FILE_PATH;
export const FLYER_IMAGE_PATH = process.env.REACT_APP_FLYER_IMAGE_PATH;
export const ADMIN_PROFILE_IMAGE_PATH = process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH;
export const ARTIST_PROFILE_IMAGE_PATH = process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH;
export const EARLIEST_CREATED_AT = '1970-01-01T00:00:00Z'; // 新規チャットでも新着取得できるようにする基準時刻
export const STALE_MS = 60 * 60 * 1000; // 60分超はフルリロード
export const EVENT_ASSIGNEE_SEARCH_STORAGE_KEY = 'admin:event-chat:assignee-search:v1';
export const UNSENDABLE_FORMATS = new Set([
  ADMIN_MESSAGE_FORMATS.TEXT,
  ADMIN_MESSAGE_FORMATS.IMAGE,
  ADMIN_MESSAGE_FORMATS.FILE,
]);

export const getCastingProgressStats = (summary) => {
  return normalizeCastingProgressSummary(summary);
};

export const getTicketProgressStats = (summary) => {
  const sold = Number(summary?.sold_total ?? summary?.soldTotal ?? 0) || 0;
  const capacity = Number(summary?.total_capacity ?? summary?.totalCapacity ?? 0) || 0;
  const pctFromSummary = Number(summary?.progress_pct ?? summary?.progressPct);
  const pct =
    capacity > 0
      ? (sold / capacity) * 100
      : Number.isFinite(pctFromSummary)
      ? pctFromSummary
      : 0;
  const percent = Math.max(0, Math.min(100, Math.round(pct || 0)));
  return { percent, sold, capacity };
};

export const getTaskProgressStats = (summary) => {
  if (!summary) return null;
  const total = Number(summary?.total ?? summary?.totalCount ?? 0) || 0;
  const open = Number(summary?.open ?? summary?.openCount ?? 0) || 0;
  const done = Number(summary?.done ?? summary?.doneCount ?? 0) || 0;
  const computedTotal = total || open + done;
  if (!computedTotal) return null;
  const percentFromSummary = Number(summary?.percent);
  const percent = Number.isFinite(percentFromSummary)
    ? Math.max(0, Math.min(100, Math.round(percentFromSummary)))
    : Math.max(0, Math.min(100, Math.round((done / computedTotal) * 100)));
  return { percent, total: computedTotal, open, done };
};

export const isActiveNotificationSilenceWindow = (windowItem, now = new Date()) => {
  if (!windowItem || windowItem.enabled === false) return false;
  const startsAt = new Date(windowItem.silentStartsAt || windowItem.silent_starts_at || '');
  const endsAt = new Date(windowItem.silentEndsAt || windowItem.silent_ends_at || '');
  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) return false;
  const nowMs = now.getTime();
  return startsAt.getTime() <= nowMs && nowMs < endsAt.getTime();
};

// 簡易 fetch + JSON パース（artist 側と同等の挙動）
export const fetchJsonSafe = createFetchJsonSafe();

/* ▼▼ 追加：パート配列の正規化ユーティリティ（null混入を除去） ▼▼ */
export const normalizePartsArray = (data) => {
  const arr =
    Array.isArray(data) ? data
    : (data && Array.isArray(data.parts)) ? data.parts
    : (data && typeof data === 'object') ? Object.values(data)
    : [];
  return arr.filter((x) => x && typeof x === 'object');
};
/* ▲▲ 追加ここまで ▲▲ */

export const {
  buildAssetUrl,
  buildFlyerUrl,
  buildMessageImageUrl,
  applyLandscapeContain,
} = createEventAssetHelpers({
  apiUrl: API_URL,
  flyerImagePath: FLYER_IMAGE_PATH,
  messageImagePath: MESSAGE_IMAGE_PATH,
});

export const formatExpireDateLabel = (value) => {
  const ymd = toDateInput(value);
  return ymd ? formatDateJP(ymd) : '';
};

/* ソケット接続 */
export const socket = getSocket();

/* 履歴 state（モバイル表示制御用 'list' を追加） */
export const pushViewState = (eventId, view) => {
  try { window.history.pushState({ scope: 'admin-event-chat', eventId, view }, '', ''); } catch {}
};
export const replaceViewState = (eventId, view) => {
  try { window.history.replaceState({ scope: 'admin-event-chat', eventId, view }, '', ''); } catch {}
};

/* =========================================================
 * コンポーネント
 * =======================================================*/
